import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product } from '@/mocks/products';

// Canvas-based comet border animation
const CometBorder = ({ width, height, radius = 16 }: { width: number; height: number; radius?: number }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const angleRef = useRef(0);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = width;
    const H = height;
    canvas.width = W;
    canvas.height = H;

    // Build perimeter path points
    const perimeter = 2 * (W + H) - 8 * radius + 2 * Math.PI * radius;

    const getPoint = (t: number): [number, number] => {
      // t: 0..1 along perimeter
      const d = t * perimeter;
      const r = radius;
      // Segments: top, top-right corner, right, bottom-right corner, bottom, bottom-left corner, left, top-left corner
      const seg = [
        W - 2 * r,                  // top straight
        (Math.PI / 2) * r,          // top-right corner
        H - 2 * r,                  // right straight
        (Math.PI / 2) * r,          // bottom-right corner
        W - 2 * r,                  // bottom straight
        (Math.PI / 2) * r,          // bottom-left corner
        H - 2 * r,                  // left straight
        (Math.PI / 2) * r,          // top-left corner
      ];
      let acc = 0;
      // top straight: left to right
      if (d < (acc + seg[0])) {
        const x = r + (d - acc);
        return [x, 0];
      }
      acc += seg[0];
      // top-right corner
      if (d < (acc + seg[1])) {
        const a = ((d - acc) / seg[1]) * (Math.PI / 2) - Math.PI / 2;
        return [W - r + Math.cos(a) * r, r + Math.sin(a) * r];
      }
      acc += seg[1];
      // right straight: top to bottom
      if (d < (acc + seg[2])) {
        const y = r + (d - acc);
        return [W, y];
      }
      acc += seg[2];
      // bottom-right corner
      if (d < (acc + seg[3])) {
        const a = ((d - acc) / seg[3]) * (Math.PI / 2);
        return [W - r + Math.cos(a) * r, H - r + Math.sin(a) * r];
      }
      acc += seg[3];
      // bottom straight: right to left
      if (d < (acc + seg[4])) {
        const x = W - r - (d - acc);
        return [x, H];
      }
      acc += seg[4];
      // bottom-left corner
      if (d < (acc + seg[5])) {
        const a = ((d - acc) / seg[5]) * (Math.PI / 2) + Math.PI / 2;
        return [r + Math.cos(a) * r, H - r + Math.sin(a) * r];
      }
      acc += seg[5];
      // left straight: bottom to top
      if (d < (acc + seg[6])) {
        const y = H - r - (d - acc);
        return [0, y];
      }
      acc += seg[6];
      // top-left corner
      const a = ((d - acc) / seg[7]) * (Math.PI / 2) + Math.PI;
      return [r + Math.cos(a) * r, r + Math.sin(a) * r];
    };

    const TAIL_LENGTH = 0.14; // fraction of perimeter - daha ince kuyruk
    const SPEED = 1 / (8000 / 16.67); // 8 saniyede tam tur

    const draw = () => {
      ctx.clearRect(0, 0, W, H);

      const headT = angleRef.current % 1;
      const tailSteps = 80;

      for (let i = 0; i <= tailSteps; i++) {
        const frac = i / tailSteps;
        const t = ((headT - frac * TAIL_LENGTH) % 1 + 1) % 1;
        const [x, y] = getPoint(t);

        // Fade: head is bright, tail fades to 0
        const alpha = Math.pow(1 - frac, 2);
        const size = frac === 0 ? 3.5 : 1 + (1 - frac) * 2;

        // Glow layers - turuncu ton
        const grd = ctx.createRadialGradient(x, y, 0, x, y, size * 2.5);
        grd.addColorStop(0, `rgba(255, 160, 60, ${alpha * 0.8})`);
        grd.addColorStop(0.5, `rgba(249, 115, 22, ${alpha * 0.35})`);
        grd.addColorStop(1, `rgba(249, 115, 22, 0)`);

        ctx.beginPath();
        ctx.arc(x, y, size * 2.5, 0, Math.PI * 2);
        ctx.fillStyle = grd;
        ctx.fill();

        // Core bright dot - ince
        ctx.beginPath();
        ctx.arc(x, y, size * 0.45, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 230, 160, ${alpha})`;
        ctx.fill();
      }

      // Head flare - parlak turuncu
      const [hx, hy] = getPoint(headT);
      const flare = ctx.createRadialGradient(hx, hy, 0, hx, hy, 10);
      flare.addColorStop(0, 'rgba(255, 255, 210, 1)');
      flare.addColorStop(0.35, 'rgba(255, 160, 60, 0.7)');
      flare.addColorStop(1, 'rgba(249, 115, 22, 0)');
      ctx.beginPath();
      ctx.arc(hx, hy, 10, 0, Math.PI * 2);
      ctx.fillStyle = flare;
      ctx.fill();

      angleRef.current = (angleRef.current + SPEED) % 1;
      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [width, height, radius]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: -4,
        left: -4,
        width: width + 8,
        height: height + 8,
        pointerEvents: 'none',
        zIndex: 20,
      }}
    />
  );
};

interface HeroSliderProps {
  products: Product[];
}

const HeroSlider = ({ products }: HeroSliderProps) => {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');
  const [progress, setProgress] = useState(0);
  const [karaokeIndex, setKaraokeIndex] = useState(0);
  const [karaokeActive, setKaraokeActive] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const karaokeRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const navigate = useNavigate();

  // Helper: get images array for a product
  const getImages = useCallback((p: Product) => {
    const imgs = (p.images && p.images.filter(Boolean).length > 0) ? p.images.filter(Boolean) : [p.image];
    return imgs;
  }, []);

  // Main (first) image of a product
  const getMainImage = useCallback((p: Product) => {
    const imgs = getImages(p);
    return imgs[0];
  }, [getImages]);

  const startKaraoke = useCallback((name: string) => {
    setKaraokeIndex(0);
    setKaraokeActive(true);
    if (karaokeRef.current) clearInterval(karaokeRef.current);
    let idx = 0;
    karaokeRef.current = setInterval(() => {
      idx += 1;
      setKaraokeIndex(idx);
      if (idx >= name.length) {
        clearInterval(karaokeRef.current!);
        karaokeRef.current = null;
        setTimeout(() => setKaraokeActive(false), 800);
      }
    }, 60);
  }, []);

  const goTo = useCallback((index: number, dir: 'next' | 'prev' = 'next') => {
    if (animating || index === current) return;
    setDirection(dir);
    setAnimating(true);
    setProgress(0);
    setPhotoIndex(0);
    setTimeout(() => {
      setCurrent(index);
      setAnimating(false);
    }, 500);
  }, [animating, current]);

  const next = useCallback(() => {
    goTo((current + 1) % products.length, 'next');
  }, [current, products.length, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + products.length) % products.length, 'prev');
  }, [current, products.length, goTo]);

  useEffect(() => {
    if (products.length === 0) return;
    setProgress(0);
    setPhotoIndex(0);
    const product = products[current];
    const imgs = getImages(product);
    const photoCount = imgs.length;
    // Total duration ~9000ms - ürünlerin tam görünmesi için uzun geçiş
    const TOTAL_MS = 9000;
    const STEPS = 90;
    // Mobile: cycle all photos; desktop bg stays on main image (handled in render)
    const photoInterval = photoCount > 1 ? Math.floor(TOTAL_MS / photoCount) : 99999;
    let photoTimer: ReturnType<typeof setInterval> | null = null;
    if (photoCount > 1) {
      let pIdx = 0;
      photoTimer = setInterval(() => {
        pIdx = (pIdx + 1) % photoCount;
        setPhotoIndex(pIdx);
      }, photoInterval);
    }
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          next();
          return 0;
        }
        return p + (100 / STEPS);
      });
    }, TOTAL_MS / STEPS);
    return () => {
      clearInterval(interval);
      if (photoTimer) clearInterval(photoTimer);
    };
  }, [current, products, next, getImages]);

  useEffect(() => {
    if (products.length === 0) return;
    const name = products[current]?.name ?? '';
    const timeout = setTimeout(() => startKaraoke(name), 550);
    return () => {
      clearTimeout(timeout);
      if (karaokeRef.current) clearInterval(karaokeRef.current);
    };
  }, [current, products, startKaraoke]);

  if (products.length === 0) {
    return (
      <div className="h-screen bg-slate-900 flex items-center justify-center">
        <p className="text-white text-xl">Slider için ürün eklenmemiş.</p>
      </div>
    );
  }

  const product = products[current];
  const currentImages = getImages(product);
  const activeImage = currentImages[photoIndex] ?? currentImages[0];

  return (
    <div className="relative h-screen min-h-[560px] max-h-[900px] overflow-hidden bg-slate-900">
      {/* Background layers - desktop: main image only, mobile: cycles all photos */}
      {products.map((p, i) => {
        const mainImg = getMainImage(p);
        const imgs = getImages(p);
        // On mobile (no lg breakpoint), cycle photos; on desktop always show main image
        const mobileBgImg = i === current ? (imgs[photoIndex] ?? imgs[0]) : imgs[0];
        return (
          <>
            {/* Desktop: static main image */}
            <div
              key={`bg-desktop-${p.id}`}
              className="absolute inset-0 transition-opacity duration-700 hidden lg:block"
              style={{
                opacity: i === current ? 1 : 0,
                backgroundImage: `url(${mainImg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center top',
                transform: i === current ? 'scale(1.05)' : 'scale(1)',
                transition: 'opacity 0.7s ease, transform 9s ease',
              }}
            />
            {/* Mobile: cycles all photos */}
            <div
              key={`bg-mobile-${p.id}`}
              className="absolute inset-0 transition-opacity duration-700 lg:hidden"
              style={{
                opacity: i === current ? 1 : 0,
                backgroundImage: `url(${mobileBgImg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center top',
                transform: i === current ? 'scale(1.05)' : 'scale(1)',
                transition: 'opacity 0.7s ease, transform 9s ease',
              }}
            />
          </>
        );
      })}

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-900/75 to-slate-900/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent" />

      {/* Animated grid lines */}
      <div className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Left: Text */}
            <div
              className={`transition-all duration-500 ${
                animating
                  ? direction === 'next'
                    ? 'opacity-0 -translate-x-8'
                    : 'opacity-0 translate-x-8'
                  : 'opacity-100 translate-x-0'
              }`}
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="inline-block bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest animate-pulse">
                  {product.category}
                </span>
                <span className="text-white/40 text-xs">
                  {String(current + 1).padStart(2, '0')} / {String(products.length).padStart(2, '0')}
                </span>
              </div>

              <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4 tracking-tight">
                {product.name.split(' ').map((word, wi, arr) => (
                  <span key={wi} className="inline-block whitespace-nowrap" style={{ marginRight: wi < arr.length - 1 ? '0.3em' : 0 }}>
                    {word.split('').map((char, ci) => {
                      const globalIdx = product.name.split(' ').slice(0, wi).join(' ').length + (wi > 0 ? 1 : 0) + ci;
                      const revealed = globalIdx < karaokeIndex;
                      return (
                        <span
                          key={ci}
                          className="inline-block transition-colors duration-150"
                          style={{
                            color: revealed ? '#fb923c' : 'white',
                            textShadow: revealed ? '0 0 20px rgba(251,146,60,0.6)' : 'none',
                          }}
                        >
                          {char}
                        </span>
                      );
                    })}
                  </span>
                ))}
              </h2>

              <p className="text-slate-300 text-sm md:text-base leading-relaxed mb-6 max-w-lg line-clamp-3">
                {product.description}
              </p>

              <div className="flex items-baseline gap-3 mb-8">
                <span className="text-4xl md:text-5xl font-extrabold text-orange-400 tracking-tight">
                  {product.price.toLocaleString('tr-TR')}
                </span>
                <span className="text-2xl font-bold text-orange-400/70">₺</span>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => navigate(`/urun/${product.id}`)}
                  className="bg-orange-500 hover:bg-orange-600 hover:scale-105 active:scale-95 text-white px-6 py-3 rounded-xl font-bold text-sm transition-all duration-200 flex items-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-eye-line"></i>
                  Ürünü İncele
                </button>
                <a
                  href={`https://wa.me/905325416344?text=Merhaba, ${encodeURIComponent(product.name)} ürünü hakkında bilgi almak istiyorum.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 hover:bg-green-700 hover:scale-105 active:scale-95 text-white px-6 py-3 rounded-xl font-bold text-sm transition-all duration-200 flex items-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line"></i>
                  WhatsApp
                </a>
                <a
                  href="/iletisim"
                  className="border border-white/30 hover:border-white/60 hover:bg-white/10 hover:scale-105 text-white px-6 py-3 rounded-xl font-bold text-sm transition-all duration-200 flex items-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line"></i>
                  İletişim
                </a>
              </div>
            </div>

            {/* Right: Product Image Card */}
            <div
              className={`hidden lg:flex justify-end transition-all duration-500 ${
                animating
                  ? direction === 'next'
                    ? 'opacity-0 translate-x-8 scale-95'
                    : 'opacity-0 -translate-x-8 scale-95'
                  : 'opacity-100 translate-x-0 scale-100'
              }`}
            >
              {/* Outer wrapper: NO overflow-hidden so canvas can bleed outside */}
              <div
                className="relative cursor-pointer group"
                style={{ width: 460, height: 340 }}
                onClick={() => navigate(`/urun/${product.id}`)}
              >
                {/* Canvas comet border - sits outside the card */}
                <CometBorder width={460} height={340} radius={16} />

                {/* Actual card with overflow-hidden */}
                <div className="absolute inset-0 rounded-2xl overflow-hidden bg-slate-800">
                  <img
                    src={activeImage}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                  />
                  {/* Photo dots indicator */}
                  {currentImages.length > 1 && (
                    <div className="absolute top-3 right-3 flex gap-1">
                      {currentImages.map((_, di) => (
                        <div key={di} className={`rounded-full transition-all duration-300 ${di === photoIndex ? 'w-4 h-1.5 bg-orange-400' : 'w-1.5 h-1.5 bg-white/40'}`} />
                      ))}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  {/* Shine effect */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)',
                    }}
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="flex items-center justify-between">
                      <span className="text-white font-bold text-sm truncate mr-3">{product.name}</span>
                      <span className="text-orange-400 font-extrabold text-lg whitespace-nowrap">
                        {product.price.toLocaleString('tr-TR')} ₺
                      </span>
                    </div>
                  </div>
                  <div className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <i className="ri-arrow-right-up-line"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 hover:bg-white/25 backdrop-blur-sm border border-white/20 rounded-full text-white transition-all duration-200 cursor-pointer hover:scale-110"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 hover:bg-white/25 backdrop-blur-sm border border-white/20 rounded-full text-white transition-all duration-200 cursor-pointer hover:scale-110"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 z-30 h-0.5 bg-white/10">
        <div
          className="h-full bg-orange-500 transition-none"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Bottom: Thumbnail Navigation */}
      <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-slate-950/80 to-transparent pt-8 pb-4">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {products.map((p, i) => (
              <button
                key={p.id}
                onClick={() => goTo(i, i > current ? 'next' : 'prev')}
                className={`flex-shrink-0 transition-all duration-300 cursor-pointer rounded-lg overflow-hidden border-2 ${
                  i === current
                    ? 'border-orange-500 opacity-100 scale-110'
                    : 'border-white/15 opacity-50 hover:opacity-75 hover:border-white/30 hover:scale-105'
                }`}
                style={{ width: 56, height: 40 }}
                title={p.name}
              >
                <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSlider;
